import React, { useState, useEffect, useRef } from "react";
import { Dialog, DialogTitle, DialogContent } from "@material-ui/core";
import { setModalOpener } from "@markedesk/plugin-sdk/ui";
import { mountPluginJsx } from "./pluginJsx";

/**
 * Host do modal de plugins. Montar UMA vez (ex: no layout/app).
 *
 * Registra `setModalOpener`: quando um script de plugin chama
 * markedesk.openHtmlModal(opts), abrimos um Dialog e renderizamos no DOM do app
 * (mesmo contexto — acesso a cookies/sessão, diferente de iframe). Dois modos:
 *  - `{ jsx, props }`  → transpila (Babel) e monta REACT de verdade (hooks). O
 *    componente recebe `props` + `{ resolve, close }` para fechar o modal.
 *  - `{ html, onMount }` → injeta HTML e chama onMount(el, resolve, close).
 */
export default function PluginHtmlModalHost() {
  const [current, setCurrent] = useState(null); // { title, html?|jsx?, props?, width, onMount?, resolve }
  // O conteúdo mora no Portal do Dialog, que só existe num render DEPOIS do
  // que abriu o modal. Com useRef o efeito via `null`, saía, e não rodava de
  // novo: modal com título e corpo vazio, sem erro (22-09-2026). Guardando o
  // nó em estado, o efeito roda quando ele aparece.
  const [contentNode, setContentNode] = useState(null);
  const jsxHandleRef = useRef(null);

  useEffect(() => {
    setModalOpener((opts) => setCurrent(opts));
    return () => setModalOpener(null);
  }, []);

  const close = (value) => {
    if (current?.resolve) current.resolve(value);
    if (jsxHandleRef.current) { jsxHandleRef.current.unmount(); jsxHandleRef.current = null; }
    setCurrent(null);
  };

  useEffect(() => {
    if (!current || !contentNode) return undefined;

    // Modo JSX: transpila e monta React de verdade (hooks funcionam).
    if (current.jsx) {
      const cache = {};
      mountPluginJsx(
        contentNode,
        current.jsx,
        { ...(current.props || {}), resolve: (v) => close(v), close: () => close(undefined) },
        cache
      ).then((handle) => { jsxHandleRef.current = handle; })
        .catch((e) => { /* eslint-disable-next-line no-console */ console.error("[PluginHtmlModalHost] erro no jsx:", e); });
      return () => { if (jsxHandleRef.current) { jsxHandleRef.current.unmount(); jsxHandleRef.current = null; } };
    }

    // Modo HTML (legado/simples): injeta innerHTML e chama onMount.
    contentNode.innerHTML = current.html || "";
    if (typeof current.onMount === "function") {
      try {
        current.onMount(
          contentNode,
          (data) => close(data),       // resolve
          () => close(undefined)       // close sem resultado
        );
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("[PluginHtmlModalHost] erro no onMount:", e);
      }
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, contentNode]);

  return (
    <Dialog
      open={!!current}
      onClose={() => close(undefined)}
      maxWidth={current?.width || "md"}
      fullWidth
    >
      {current?.title && <DialogTitle>{current.title}</DialogTitle>}
      <DialogContent dividers>
        <div ref={setContentNode} />
      </DialogContent>
    </Dialog>
  );
}
